import { useState, useEffect, useCallback } from 'react';
import { useEffectiveBusinessId } from '@/hooks';
import {
  getUnassignedReceipts,
  updateReceiptAssignments,
  markReceiptUnassigned,
  getReceiptUrl,
} from '../services/receiptService';
import type { UnassignedReceiptView } from '../types';

interface UseUnassignedReceiptsReturn {
  receipts: UnassignedReceiptView[];
  isLoading: boolean;
  error: string | null;
  assignReceipt: (
    documentId: string,
    projectId: string | null,
    supplierId: string | null
  ) => Promise<void>;
  dismissReceipt: (documentId: string) => Promise<void>;
  getSignedUrl: (filePath: string) => Promise<string>;
  refetch: () => Promise<void>;
}

export function useUnassignedReceipts(): UseUnassignedReceiptsReturn {
  const effectiveBusinessId = useEffectiveBusinessId();
  const [receipts, setReceipts] = useState<UnassignedReceiptView[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchReceipts = useCallback(async () => {
    if (!effectiveBusinessId) {
      setReceipts([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await getUnassignedReceipts(effectiveBusinessId);
      setReceipts(data);
    } catch (err) {
      console.error('Error fetching unassigned receipts:', err);
      setError(err instanceof Error ? err.message : 'Failed to load receipts');
    } finally {
      setIsLoading(false);
    }
  }, [effectiveBusinessId]);

  useEffect(() => {
    fetchReceipts();
  }, [fetchReceipts]);

  const assignReceipt = useCallback(
    async (documentId: string, projectId: string | null, supplierId: string | null) => {
      if (!effectiveBusinessId) return;

      try {
        await updateReceiptAssignments(documentId, effectiveBusinessId, projectId, supplierId);
        // Remove from list if assigned to a project
        if (projectId) {
          setReceipts((prev) => prev.filter((r) => r.id !== documentId));
        }
      } catch (err) {
        console.error('Error assigning receipt:', err);
        throw err;
      }
    },
    [effectiveBusinessId]
  );

  const dismissReceipt = useCallback(
    async (documentId: string) => {
      if (!effectiveBusinessId) return;

      try {
        await markReceiptUnassigned(documentId, effectiveBusinessId);
        setReceipts((prev) => prev.filter((r) => r.id !== documentId));
      } catch (err) {
        console.error('Error dismissing receipt:', err);
        throw err;
      }
    },
    [effectiveBusinessId]
  );

  const getSignedUrl = useCallback(async (filePath: string) => {
    return getReceiptUrl(filePath);
  }, []);

  return {
    receipts,
    isLoading,
    error,
    assignReceipt,
    dismissReceipt,
    getSignedUrl,
    refetch: fetchReceipts,
  };
}